import React, { useState, useEffect } from "react";
import { Card, CardHeader, CardBody, Spinner } from "reactstrap";
import ReactApexChart from "react-apexcharts";
import { fetchAPI } from "../utils/api";
import { threatCard, threatCardHeader, threatCardBody, threatIconChip } from "./cardStyles";

const ORANGE_COLORS = {
  primary: '#FF5B00',
  dark: '#e85a28',
  darker: '#d14d1f',
  light: '#ff8c61',
  lighter: '#ffb399',
  subtle: '#fff5f0',
  muted: '#ffe8dd',
};

// Orange gradient colors for compliance frameworks
const FRAMEWORK_COLORS = [
  '#FF5B00',  // Primary orange
  '#ff8c61',  // Light orange
  '#e85a28',  // Dark orange
  '#ffb399',  // Lighter orange
  '#d14d1f',  // Darker orange
  '#ff9d7a',  // Mid-light orange
  '#c94219',  // Deep orange
  '#ffac8a',  // Soft orange
  '#b33a15',  // Very dark orange
  '#ffc3b0',  // Very light orange
];

const ComplianceViolations = ({ loading: parentLoading }) => {
  const [complianceData, setComplianceData] = useState(null);
  const [complianceLoading, setComplianceLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchComplianceData();
  }, []);

  const fetchComplianceData = async () => {
    try {
      const response = await fetchAPI('/threats/compliance-mappings');
      
      console.log("✅ Compliance API Response:", response);
      
      if (response.success && response.data) {
        const frameworks = {};
        
        response.data.forEach(mapping => {
          const framework = mapping.framework || 'Unknown';
          frameworks[framework] = (frameworks[framework] || 0) + 1;
        });
        
        console.log("✅ Grouped frameworks:", frameworks);
        setComplianceData(frameworks);
      } else {
        console.warn("⚠️ Invalid API response structure:", response);
        setComplianceData({});
      }
    } catch (err) {
      console.error("❌ Error fetching compliance data:", err);
      setError(err.message);
      setComplianceData({});
    } finally {
      setComplianceLoading(false);
    }
  };

  if (parentLoading || complianceLoading) {
    return (
      <Card 
        className="h-100"
        style={{ 
          ...threatCard
        }}
      >
        <CardHeader style={threatCardHeader}>
          <div className="d-flex align-items-center gap-3">
            <div style={threatIconChip()}>
              <i className="ri-file-shield-line" style={{ color: "var(--vz-primary)", fontSize: 16 }}></i>
            </div>
            <h5 className="card-title mb-0">Compliance Mappings</h5>
          </div>
        </CardHeader>
        <CardBody className="d-flex justify-content-center align-items-center" style={{ minHeight: "250px", ...threatCardBody }}>
          <Spinner style={{ color: ORANGE_COLORS.primary }} />
        </CardBody>
      </Card>
    );
  }

  if (error) {
    return (
      <Card 
        className="h-100"
        style={{ 
          ...threatCard
        }}
      >
        <CardHeader style={threatCardHeader}>
          <div className="d-flex align-items-center gap-3">
            <div style={threatIconChip()}>
              <i className="ri-file-shield-line" style={{ color: "var(--vz-primary)", fontSize: 16 }}></i>
            </div>
            <h5 className="card-title mb-0">Compliance Mappings</h5>
          </div>
        </CardHeader>
        <CardBody className="d-flex justify-content-center align-items-center" style={{ minHeight: "250px", ...threatCardBody }}>
          <div className="text-center" style={{ color: '#dc3545' }}>
            <i className="ri-error-warning-line fs-1 mb-2 d-block"></i>
            <p className="mb-0 fw-semibold">Failed to load compliance data</p>
            <small className="text-muted">{error}</small>
          </div>
        </CardBody>
      </Card>
    );
  }

  const frameworks = Object.keys(complianceData || {});
  const counts = frameworks.map(f => complianceData[f]);
  const total = counts.reduce((sum, c) => sum + c, 0);

  // Assign orange colors to frameworks
  const colors = frameworks.map((_, index) => FRAMEWORK_COLORS[index % FRAMEWORK_COLORS.length]);

  const options = {
    chart: {
      type: "donut",
      toolbar: { show: false },
      fontFamily: "Poppins, sans-serif",
      animations: {
        enabled: true,
        easing: 'easeinout',
        speed: 800
      }
    },
    labels: frameworks,
    colors: colors,
    dataLabels: {
      enabled: true,
      formatter: (val) => `${Math.round(val)}%`,
      style: {
        fontSize: '11px',
        fontWeight: '600',
        colors: ['#fff']
      },
      dropShadow: {
        enabled: true,
        top: 1,
        left: 1,
        blur: 1,
        opacity: 0.45
      }
    },
    legend: {
      position: "bottom",
      fontSize: "11px",
      markers: {
        width: 10,
        height: 10,
        radius: 2
      },
      itemMargin: {
        horizontal: 8,
        vertical: 4
      },
      labels: {
        colors: '#858d98'
      }
    },
    plotOptions: {
      pie: {
        donut: {
          size: "70%",
          labels: {
            show: true,
            total: {
              show: true,
              label: "Total",
              fontSize: "14px",
              fontWeight: 600,
              color: ORANGE_COLORS.primary,
              formatter: () => total.toString()
            },
            value: {
              fontSize: '24px',
              fontWeight: 700,
              color: ORANGE_COLORS.primary,
              formatter: (val) => val
            }
          }
        }
      }
    },
    tooltip: {
      theme: 'dark',
      enabled: true,
      y: {
        formatter: function(value) {
          const percentage = ((value / total) * 100).toFixed(1);
          return `${value} (${percentage}%)`;
        }
      },
      style: {
        fontSize: '12px'
      }
    },
    stroke: {
      show: true,
      width: 2,
      colors: ['transparent']
    },
    states: {
      hover: {
        filter: {
          type: 'lighten',
          value: 0.1
        }
      }
    }
  };

  const series = counts;

  return (
    <Card 
      className="h-100"
      style={{ 
        ...threatCard
      }}
    >
      <CardHeader style={threatCardHeader}>
        <div className="d-flex justify-content-between align-items-center">
          <div className="d-flex align-items-center gap-3">
            <div style={threatIconChip()}>
              <i className="ri-file-shield-line" style={{ color: "var(--vz-primary)", fontSize: 16 }}></i>
            </div>
            <h5 className="card-title mb-0">Compliance Mappings</h5>
          </div>
          {total > 0 && (
            <span 
              className="badge" 
              style={{ 
                backgroundColor: 'rgba(255, 107, 53, 0.1)', 
                color: ORANGE_COLORS.primary,
                border: `1px solid ${ORANGE_COLORS.primary}`,
                fontSize: '11px'
              }}
            >
              {total} Total
            </span>
          )}
        </div>
      </CardHeader>
      <CardBody style={threatCardBody}>
        {frameworks.length > 0 ? (
          <ReactApexChart 
            options={options} 
            series={series} 
            type="donut" 
            height={300} 
          />
        ) : (
          <div className="text-center py-5">
            <i className="ri-file-shield-line display-4 mb-3 d-block" style={{ color: ORANGE_COLORS.light }}></i>
            <p className="text-muted mb-0 fw-semibold">No compliance mappings found</p>
            <small className="text-muted">Compliance data will appear here once threats are detected</small>
          </div>
        )}
      </CardBody>
    </Card>
  );
};

export default ComplianceViolations;
